import { useFlag } from '@reflag/react-sdk';

import type * as api from '@gitbook/api-client';
import { createPalette, PaletteButton, PaletteCommandType } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { type ButtonProps, DialogRoot, useDialogTriggerState } from '@gitbook/spine-react';

import { useNavigation } from '../../../navigation';
import { apiClient, type SiteCapability, type UseUpgradeSite } from '../../../state';
import { useSiteAudienceCommand } from '../../AdaptiveContent';
import { CreateSiteSectionGroupDialog } from './CreateSiteSectionGroupDialog';
import {
    CreateSiteVariantDialog,
    type CreateSiteVariantDialogProps,
} from './CreateSiteVariantDialog';
import { getPaletteLocationOptions } from './getPaletteLocationOptions';
import { getPaletteSpaceOptions } from './getPaletteSpaceOptions';
import { type UseDeleteSiteItemsProps, useDeleteSiteItems } from './useDeleteSiteItems';
import { type UseMoveSiteSectionsProps, useMoveSiteSections } from './useMoveSiteSections';
import {
    type UseSetDefaultSiteSectionProps,
    useSetDefaultSiteSection,
} from './useSetDefaultSiteSection';
import { useSetSiteSpaceVisibility } from './useSetSiteSpaceVisibility';

export interface SiteSectionActionsButtonProps
    extends CreateSiteVariantDialogProps,
        Pick<ButtonProps, 'size' | 'disabled'>,
        Omit<UseMoveSiteSectionsProps, 'createSiteSectionGroupDialogTrigger'>,
        Pick<CreateSiteVariantDialogProps, 'onCreateVariant'>,
        UseDeleteSiteItemsProps,
        UseSetDefaultSiteSectionProps {
    site: api.Site;
    organization: api.Organization;
    siteSection: api.SiteSection;
    sitePermissions: SiteCapability;
    siteStructure: api.SiteStructure;
    upgrade: UseUpgradeSite | null;
}

/**
 * Action button to open options palette for a section
 */
export function SiteSectionActionsButton(props: SiteSectionActionsButtonProps) {
    const { site, organization, siteSection, sitePermissions, size, disabled, onCreateVariant } =
        props;
    const { canAdminSite } = sitePermissions;
    const organizationId = organization.id;
    const hideSiteSectionFeature = useFlag('HIDDEN_SITES_SPACE');

    const { navigate } = useNavigation();
    const createSiteVariantDialogTrigger = useDialogTriggerState();
    const createSiteSectionGroupDialogTrigger = useDialogTriggerState();

    const deleteSiteItems = useDeleteSiteItems(props);
    const moveSiteSections = useMoveSiteSections({ ...props, createSiteSectionGroupDialogTrigger });
    const setAsDefault = useSetDefaultSiteSection(props);
    const siteAudience = useSiteAudienceCommand({
        organizationId,
        site,
        target: 'section',
        condition: siteSection.condition ? siteSection.condition : undefined,
        onChange: async (condition) => {
            await apiClient.siteSections.updateSiteSectionById(
                organizationId,
                site.id,
                siteSection.id,
                {
                    condition: condition ?? null,
                }
            );
        },
    });
    const singleSiteSpace = siteSection.siteSpaces.length === 1 ? siteSection.siteSpaces[0] : null;
    const areAllSiteSpacesHidden =
        siteSection.siteSpaces.length > 0 &&
        siteSection.siteSpaces.every((siteSpace) => siteSpace.hidden);

    const setSiteVariantVisibility = useSetSiteSpaceVisibility({
        organization,
        site,
        type: 'section',
    });

    return (
        <>
            {siteAudience?.dialogs}

            <DialogRoot {...createSiteVariantDialogTrigger.rootProps}>
                <CreateSiteVariantDialog {...props} onCreateVariant={onCreateVariant} />
            </DialogRoot>

            <DialogRoot {...createSiteSectionGroupDialogTrigger.rootProps}>
                <CreateSiteSectionGroupDialog
                    organization={organization}
                    site={site}
                    siteSections={[siteSection]}
                />
            </DialogRoot>

            <PaletteButton
                palette={() => {
                    return createPalette({
                        groups: [
                            singleSiteSpace
                                ? getPaletteSpaceOptions({ space: singleSiteSpace.space, navigate })
                                : null,
                            siteSection.urls.published
                                ? getPaletteLocationOptions(siteSection.urls.published)
                                : null,
                            canAdminSite
                                ? {
                                      key: 'create-variant',
                                      commands: [
                                          {
                                              key: 'siteSection.createVariant',
                                              title: 'Add variant',
                                              icon: Icon.Plus,
                                              trigger: () => createSiteVariantDialogTrigger.open(),
                                          },
                                      ],
                                  }
                                : null,
                            canAdminSite
                                ? {
                                      key: 'update-options',
                                      commands: [
                                          {
                                              key: 'siteSection.setDefault',
                                              title: 'Set as home',
                                              icon: Icon.Home,
                                              disabled:
                                                  Boolean(siteSection.default) ||
                                                  areAllSiteSpacesHidden,
                                              subtitle: areAllSiteSpacesHidden
                                                  ? 'Hidden from site navigation'
                                                  : siteSection.default
                                                    ? 'Already set as home'
                                                    : undefined,
                                              trigger: () => setAsDefault(siteSection),
                                          },
                                          hideSiteSectionFeature.isEnabled && singleSiteSpace
                                              ? {
                                                    key: 'siteSection.hide',
                                                    enabled: !singleSiteSpace.hidden,
                                                    disabled: Boolean(siteSection.default),
                                                    subtitle: siteSection.default
                                                        ? "Home section can't be hidden"
                                                        : undefined,
                                                    type: PaletteCommandType.SWITCH as const,
                                                    title: 'Show in site navigation',
                                                    icon: Icon.Eye,
                                                    trigger: () => {
                                                        setSiteVariantVisibility(singleSiteSpace);
                                                    },
                                                }
                                              : null,
                                          siteAudience?.command ?? null,
                                          {
                                              key: 'siteSection.addToGroup',
                                              title: 'Move to…',
                                              icon: Icon.ArrowRight,
                                              descendant: () => moveSiteSections([siteSection]),
                                          },
                                          {
                                              key: 'siteSection.delete',
                                              title: 'Remove',
                                              icon: Icon.Trashcan,
                                              kind: 'danger',
                                              trigger: () => deleteSiteItems([siteSection]),
                                          },
                                      ].filter((x) => x !== null),
                                  }
                                : null,
                        ].filter((x) => x !== null),
                    });
                }}
                kind="nested"
                label="Actions"
                leadingIcon={Icon.MoreVertical}
                iconOnly
                size={size}
                disabled={disabled}
            />
        </>
    );
}
